import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { Ban, Percent, PlugZap, RotateCcw, Star } from "lucide-react";
import { toast } from "sonner";
import type { AdminPaymentProviderItem, PaymentMethodKind } from "@ticketnjoy/types";

import { QueryError } from "@/components/query-error";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  useIsSuperAdmin,
  useMakeDefaultProviderMutation,
  usePaymentProvidersQuery,
  useSetProviderAvailabilityMutation,
  useTestProviderMutation,
  useUpdateProviderCardFeeMutation,
} from "@/features/api";
import { showApiError } from "@/lib/api-client";
import { dateTime, parseDecimal, percent } from "@/lib/format";

const METHOD_LABELS: Record<PaymentMethodKind, string> = {
  CreditCard: "Kredi kartı",
  Wallet: "Cüzdan",
  Mock: "Test ödemesi",
};

type DialogState =
  | { kind: "disable" | "enable" | "default" | "fee"; provider: AdminPaymentProviderItem }
  | null;

export function PaymentSettingsPage() {
  const isSuper = useIsSuperAdmin();
  const { data, isLoading, error, refetch, isRefetching } = usePaymentProvidersQuery();
  const test = useTestProviderMutation();
  const [dialog, setDialog] = useState<DialogState>(null);

  async function onTest(provider: AdminPaymentProviderItem) {
    try {
      const items = await test.mutateAsync(provider.key);
      const health = items.find((p) => p.key === provider.key)?.health;
      if (health?.lastCheckOk) toast.success(`${provider.displayName}: ${health.lastCheckMessage}`);
      else toast.error(`${provider.displayName}: ${health?.lastCheckMessage ?? "Test başarısız."}`);
    } catch (err) {
      showApiError(err);
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Ödeme ayarları</h1>
        <p className="text-sm text-muted-foreground">
          TicketNjoy'un POS'ları. Organizatör yalnız "Kredi Kartı"nı görür; çekim organizatöre atanmış
          POS'tan, atanmamışsa varsayılan POS'tan yapılır. Değişiklikler yeni ödemelere uygulanır;
          tamamlanmış siparişin kart komisyonu değişmez.
        </p>
      </div>

      <Card className="py-0">
        <CardContent className="px-0">
          {error ? (
            <QueryError
              className="m-6"
              message="Ödeme sağlayıcıları yüklenemedi."
              onRetry={() => refetch()}
              retrying={isRefetching}
            />
          ) : isLoading || !data ? (
            <p className="p-6 text-sm text-muted-foreground">Yükleniyor...</p>
          ) : (
            <ul className="divide-y text-sm">
              {data.map((p) => (
                <ProviderRow
                  key={p.key}
                  provider={p}
                  isSuper={isSuper}
                  onDialog={setDialog}
                  onTest={() => onTest(p)}
                  testPending={test.isPending && test.variables === p.key}
                />
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {!isSuper && (
        <p className="text-xs text-muted-foreground">Değiştirmek ve bağlantı testi için süper admin yetkisi gerekir.</p>
      )}

      {dialog?.kind === "fee" ? (
        <CardFeeDialog provider={dialog.provider} onClose={() => setDialog(null)} />
      ) : dialog ? (
        <ConfirmDialog
          kind={dialog.kind}
          provider={dialog.provider}
          providers={data ?? []}
          onClose={() => setDialog(null)}
        />
      ) : null}
    </div>
  );
}

function ProviderRow({
  provider,
  isSuper,
  onDialog,
  onTest,
  testPending,
}: {
  provider: AdminPaymentProviderItem;
  isSuper: boolean;
  onDialog: (state: DialogState) => void;
  onTest: () => void;
  testPending: boolean;
}) {
  const { health } = provider;
  const isCard = provider.methodKind === "CreditCard";

  return (
    <li className="flex flex-wrap items-start justify-between gap-4 px-6 py-4">
      <div className="min-w-0 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium">{provider.displayName}</span>
          <Badge variant="outline">{METHOD_LABELS[provider.methodKind]}</Badge>
          {isCard && provider.isDefault && <Badge variant="outline">Varsayılan</Badge>}
          {!provider.ready ? (
            <Badge variant="secondary">Entegrasyon yok</Badge>
          ) : provider.isAvailable ? (
            <Badge variant="success">Açık</Badge>
          ) : (
            <Badge variant="destructive">Kapalı</Badge>
          )}
        </div>
        {isCard && (
          <div className="text-xs text-muted-foreground">
            Kart komisyonu {percent(provider.cardFeeRate)}
            {provider.assignedOrganizerCount > 0 && ` · ${provider.assignedOrganizerCount} organizatöre atanmış`}
          </div>
        )}
        {provider.ready && (
          <>
            <div className="text-xs text-muted-foreground">
              Son başarılı ödeme: {dateTime(health.lastPaidAt)} · Son başarısız: {dateTime(health.lastFailedAt)}
            </div>
            <div className="text-xs text-muted-foreground">
              Son 24 saat: {health.paidLast24h} başarılı, {health.failedLast24h} başarısız
              {health.openIssueCount > 0 && (
                <>
                  {" · "}
                  <Link
                    to={`/orders?problem=true&provider=${provider.key}`}
                    className="text-destructive hover:underline"
                  >
                    {health.openIssueCount} sorunlu ödeme
                  </Link>
                </>
              )}
            </div>
            <div className="text-xs text-muted-foreground">
              {health.lastCheckedAt ? (
                <>
                  Bağlantı testi ({dateTime(health.lastCheckedAt)}):{" "}
                  <span className={health.lastCheckOk ? "text-foreground" : "text-destructive"}>
                    {health.lastCheckMessage}
                  </span>
                </>
              ) : (
                "Bağlantı testi yapılmadı"
              )}
            </div>
          </>
        )}
      </div>

      {isSuper && provider.ready && (
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={onTest} disabled={testPending}>
            <PlugZap className="size-4" />
            {testPending ? "Test ediliyor..." : "Bağlantıyı test et"}
          </Button>
          {isCard && (
            <Button variant="outline" size="sm" onClick={() => onDialog({ kind: "fee", provider })}>
              <Percent className="size-4" />
              Komisyonu değiştir
            </Button>
          )}
          {isCard && provider.isAvailable && !provider.isDefault && (
            <Button variant="outline" size="sm" onClick={() => onDialog({ kind: "default", provider })}>
              <Star className="size-4" />
              Varsayılan yap
            </Button>
          )}
          {provider.isAvailable ? (
            <Button
              variant="outline"
              size="sm"
              className="text-destructive"
              onClick={() => onDialog({ kind: "disable", provider })}
            >
              <Ban className="size-4" />
              Kapat
            </Button>
          ) : (
            <Button variant="outline" size="sm" onClick={() => onDialog({ kind: "enable", provider })}>
              <RotateCcw className="size-4" />
              Aç
            </Button>
          )}
        </div>
      )}
    </li>
  );
}

/** Kart POS'u kapanınca o POS'u kullananların düşeceği POS: açık varsayılan, yoksa açık ilk kart POS'u. */
function fallbackFor(provider: AdminPaymentProviderItem, providers: AdminPaymentProviderItem[]) {
  const open = providers.filter(
    (p) => p.methodKind === "CreditCard" && p.isAvailable && p.key !== provider.key,
  );
  return open.find((p) => p.isDefault) ?? open[0] ?? null;
}

function ConfirmDialog({
  kind,
  provider,
  providers,
  onClose,
}: {
  kind: "disable" | "enable" | "default";
  provider: AdminPaymentProviderItem;
  providers: AdminPaymentProviderItem[];
  onClose: () => void;
}) {
  const setAvailability = useSetProviderAvailabilityMutation();
  const makeDefault = useMakeDefaultProviderMutation();
  const [reason, setReason] = useState("");
  const pending = setAvailability.isPending || makeDefault.isPending;
  const isCard = provider.methodKind === "CreditCard";
  const fallback = isCard ? fallbackFor(provider, providers) : null;

  const title =
    kind === "disable"
      ? `${provider.displayName} kapatılsın mı?`
      : kind === "enable"
        ? `${provider.displayName} açılsın mı?`
        : `${provider.displayName} varsayılan yapılsın mı?`;

  const description =
    kind === "default"
      ? "POS atanmamış organizatörlerin kart ödemeleri bu POS'tan çekilir."
      : kind === "enable"
        ? "Yeni ödemelerde tekrar seçilebilir; bu POS'a atanmış organizatörler ona geri döner."
        : !isCard
          ? "Bu yöntemle yeni ödeme başlatılamaz. Başlamış ödemelerin dönüşü işlenmeye devam eder."
          : fallback
            ? `Bu POS'u kullanan organizatörlerin yeni kart ödemeleri ${fallback.displayName} üzerinden çekilir. Başlamış ödemelerin dönüşü işlenmeye devam eder.`
            : "Açık başka kart POS'u yok: kartla satış tüm organizatörlerde durur. Başlamış ödemelerin dönüşü işlenmeye devam eder.";

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    try {
      if (kind === "default") {
        await makeDefault.mutateAsync(provider.key);
        toast.success(`Varsayılan kart POS'u: ${provider.displayName}`);
      } else {
        await setAvailability.mutateAsync({
          key: provider.key,
          isAvailable: kind === "enable",
          reason: reason.trim() || null,
        });
        toast.success(`${provider.displayName} ${kind === "enable" ? "açıldı" : "kapatıldı"}.`);
      }
      onClose();
    } catch (err) {
      showApiError(err);
    }
  }

  return (
    <Dialog open onOpenChange={(next) => !next && onClose()} disableBackdropClose>
      <DialogContent>
        <DialogHeader title={title} description={description} />
        <form onSubmit={onSubmit} className="space-y-4">
          {kind === "disable" && (
            <div className="space-y-2">
              <Label htmlFor="provider-reason">Sebep (isteğe bağlı)</Label>
              <Textarea
                id="provider-reason"
                maxLength={500}
                rows={2}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">Yalnız platform kayıtlarında görünür.</p>
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Vazgeç
            </Button>
            <Button type="submit" variant={kind === "disable" ? "destructive" : "default"} disabled={pending}>
              {pending ? "Kaydediliyor..." : kind === "disable" ? "Kapat" : kind === "enable" ? "Aç" : "Varsayılan yap"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CardFeeDialog({ provider, onClose }: { provider: AdminPaymentProviderItem; onClose: () => void }) {
  const update = useUpdateProviderCardFeeMutation();
  const [rate, setRate] = useState(String(provider.cardFeeRate));
  const value = parseDecimal(rate);
  const valid = value !== null && value <= 99.99;
  const dirty = valid && value !== provider.cardFeeRate;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!dirty || value === null) return;
    try {
      await update.mutateAsync({ key: provider.key, cardFeeRate: value });
      toast.success(`${provider.displayName} kart komisyonu ${percent(value)} oldu.`);
      onClose();
    } catch (err) {
      showApiError(err);
    }
  }

  return (
    <Dialog open onOpenChange={(next) => !next && onClose()} disableBackdropClose>
      <DialogContent>
        <DialogHeader
          title={`${provider.displayName} kart komisyonu`}
          description="Organizatörün net gelirinden düşülür. Yeni ödemelere uygulanır; özel oranı olan organizatörler etkilenmez."
        />
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="provider-card-fee">Kart komisyonu (%)</Label>
            <Input id="provider-card-fee" inputMode="decimal" value={rate} onChange={(e) => setRate(e.target.value)} />
            {!valid && <p className="text-xs text-destructive">Oran 0 ile 99,99 arasında olmalı.</p>}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Vazgeç
            </Button>
            <Button type="submit" disabled={!dirty || update.isPending}>
              {update.isPending ? "Kaydediliyor..." : "Kaydet"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
